<template>
  <div class="campaign-data-page">
    <a-row :gutter="[24, 24]" type="flex">
      <a-col :span="24">
        <a-card :bordered="false" class="header-solid filter-card" :bodyStyle="{ padding: '20px 24px' }">
          <div class="filter-card__title">
            <h5 class="font-semibold m-0">Campaign Data</h5>
            <span class="filter-card__hint">Choose a campaign and date range, then load results.</span>
          </div>
          <a-row type="flex" :gutter="[16, 16]" align="middle" class="filter-row">
            <a-col :xs="24" :sm="24" :md="8" :lg="7">
              <div class="field-label">Campaign</div>
              <a-select
                v-model="selectedCampaign"
                placeholder="Select a campaign"
                class="w-100"
                :loading="loadingCampaigns"
                allow-clear
                show-search
                :filter-option="filterCampaignOption"
                option-filter-prop="children"
              >
                <a-select-option
                  v-for="opt in campaignSelectOptions"
                  :key="opt.value"
                  :value="opt.value"
                >
                  {{ opt.label }}
                </a-select-option>
              </a-select>
            </a-col>
            <a-col :xs="24" :sm="24" :md="10" :lg="10">
              <div class="field-label">Date range</div>
              <a-range-picker
                v-model="dateRange"
                class="w-100"
                format="YYYY-MM-DD"
                :placeholder="['Start date', 'End date']"
                @change="onDateRangeChange"
              />
            </a-col>
            <a-col :xs="24" :sm="24" :md="6" :lg="7" class="submit-col">
              <div class="field-label field-label--placeholder">&nbsp;</div>
              <a-button type="primary" size="large" block :loading="loadingData" @click="submitFetch">
                Submit
              </a-button>
            </a-col>
          </a-row>
        </a-card>
      </a-col>

      <a-col :span="24">
        <a-card :bordered="false" class="header-solid results-card" :bodyStyle="{ padding: 0 }">
          <template #title>
            <span class="results-card__title">Results</span>
            <a-tag v-if="resultMetaText" color="blue" class="ml-2">{{ resultMetaText }}</a-tag>
          </template>
          <div v-if="loadingData" class="results-state">
            <a-spin tip="Loading campaign data…" />
          </div>
          <template v-else>
            <a-empty
              v-if="showEmptyState"
              class="results-empty"
              image="simple"
              description="No Data Found"
            />
            <div v-else class="table-wrap">
              <a-table
                :columns="tableColumns"
                :data-source="tableRows"
                :pagination="tablePagination"
                :scroll="{ x: 'max-content' }"
                row-key="key"
                size="middle"
                :bordered="false"
                class="campaign-data-table"
              />
            </div>
          </template>
        </a-card>
      </a-col>
    </a-row>
  </div>
</template>

<script>
import axios from 'axios';

const FALLBACK_API_BASE = 'https://developer.quizpro.gamehubbd.com/api/v2.0/admin';

function apiBase() {
  const raw =
    process.env.VUE_APP_CAMPAIGN_API_BASE ||
    process.env.VUE_APP_ADMIN_URL ||
    FALLBACK_API_BASE;
  return String(raw).replace(/\/+$/, '');
}

function extractArray(payload) {
  if (payload == null) return [];
  if (Array.isArray(payload)) return payload;
  if (typeof payload !== 'object') return [];
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.campaigns)) return payload.campaigns;
  if (payload.data && typeof payload.data === 'object' && Array.isArray(payload.data.data)) {
    return payload.data.data;
  }
  return [];
}

function normalizeCampaignOptions(rawList) {
  return rawList
    .map((item) => {
      if (item == null) return null;
      if (typeof item === 'string' || typeof item === 'number') {
        const v = String(item).trim();
        return v ? { value: v, label: v } : null;
      }
      if (typeof item === 'object') {
        const value =
          item.campaign ??
          item.slug ??
          item.name ??
          item.code ??
          item.id ??
          item.value;
        if (value == null || String(value).trim() === '') return null;
        const str = String(value).trim();
        const label =
          item.label ??
          item.title ??
          item.display_name ??
          item.name ??
          str;
        return { value: str, label: String(label) };
      }
      return null;
    })
    .filter(Boolean);
}

/** @param {*} serverBody Parsed JSON body from axios response.data */
function extractRows(serverBody) {
  const d = serverBody;
  if (d == null) return [];
  if (Array.isArray(d)) return d;
  if (typeof d !== 'object') return [];
  if (Array.isArray(d.data)) return d.data;
  if (d.data && typeof d.data === 'object' && Array.isArray(d.data.rows)) return d.data.rows;
  if (Array.isArray(d.rows)) return d.rows;
  if (Array.isArray(d.items)) return d.items;
  return [];
}

function humanizeKey(key) {
  return String(key)
    .replace(/_/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export default {
  name: 'CampaignData',
  data() {
    return {
      loadingCampaigns: false,
      loadingData: false,
      campaignSelectOptions: [],
      selectedCampaign: undefined,
      dateRange: [],
      startDate: '',
      endDate: '',
      tableRows: [],
      tableColumns: [],
      hasSubmitted: false,
      resultSummary: null,
    };
  },
  computed: {
    showEmptyState() {
      return this.hasSubmitted && !this.loadingData && this.tableRows.length === 0;
    },
    resultMetaText() {
      if (!this.hasSubmitted || this.loadingData) return '';
      const c = this.resultSummary && this.resultSummary.campaign;
      const s = this.resultSummary && this.resultSummary.startDate;
      const e = this.resultSummary && this.resultSummary.endDate;
      if (!c || !s || !e) return '';
      return `${c} · ${s} → ${e}`;
    },
    tablePagination() {
      const n = this.tableRows.length;
      if (n <= 10) return false;
      return { pageSize: 10, showSizeChanger: true, showTotal: (t) => `${t} records` };
    },
  },
  mounted() {
    this.fetchDistinctCampaigns();
  },
  methods: {
    filterCampaignOption(input, option) {
      const opt = this.campaignSelectOptions.find((o) => o.value === option.componentOptions.propsData.value);
      if (!opt) return false;
      const q = (input || '').toLowerCase();
      return opt.label.toLowerCase().includes(q) || opt.value.toLowerCase().includes(q);
    },
    onDateRangeChange(_moments, dateStrings) {
      this.startDate = (dateStrings && dateStrings[0]) || '';
      this.endDate = (dateStrings && dateStrings[1]) || '';
    },
    async fetchDistinctCampaigns() {
      this.loadingCampaigns = true;
      const base = apiBase();
      try {
        const { data } = await axios.get(`${base}/distinct-campaign`);
        const list = extractArray(data);
        this.campaignSelectOptions = normalizeCampaignOptions(list);
      } catch (e) {
        console.error(e);
        this.$message.error('Could not load campaigns. Check network or API configuration.');
        this.campaignSelectOptions = [];
      } finally {
        this.loadingCampaigns = false;
      }
    },
    submitFetch() {
      if (!this.selectedCampaign) {
        this.$message.warning('Please select a campaign.');
        return;
      }
      if (!this.startDate || !this.endDate) {
        this.$message.warning('Please select a start and end date.');
        return;
      }
      this.loadCampaignData();
    },
    async loadCampaignData() {
      const base = apiBase();
      const params = {
        campaign: this.selectedCampaign,
        startDate: this.startDate,
        endDate: this.endDate,
      };
      this.loadingData = true;
      this.hasSubmitted = true;
      try {
        const { data } = await axios.get(`${base}/campaign-data`, { params });
        const rows = extractRows(data);
        const normalized = Array.isArray(rows) ? rows : [];
        this.resultSummary = {
          campaign: params.campaign,
          startDate: params.startDate,
          endDate: params.endDate,
        };
        this.buildTable(normalized);
      } catch (e) {
        console.error(e);
        this.tableRows = [];
        this.tableColumns = [];
        this.$message.error('Failed to load campaign data.');
      } finally {
        this.loadingData = false;
      }
    },
    buildTable(rows) {
      if (!rows.length) {
        this.tableRows = [];
        this.tableColumns = [];
        return;
      }
      const first = rows[0];
      if (typeof first !== 'object' || first === null) {
        this.tableRows = rows.map((cell, i) => ({
          key: `row-${i}`,
          value: cell,
        }));
        this.tableColumns = [
          {
            title: 'Value',
            dataIndex: 'value',
            key: 'value',
            ellipsis: true,
          },
        ];
        return;
      }
      const keys = Object.keys(first).filter((k) => k !== 'key');
      const cols = keys.map((k) => {
        const isComplex = rows.some(
          (r) => r[k] !== null && r[k] !== undefined && typeof r[k] === 'object'
        );
        return {
          title: humanizeKey(k),
          dataIndex: k,
          key: k,
          ellipsis: true,
          customRender: (text) => {
            const out = this.formatCellValue(text);
            return isComplex
              ? this.$createElement('span', { class: 'cell-complex' }, [out])
              : out;
          },
          sorter: isComplex
            ? false
            : (a, b) => {
                const va = a[k];
                const vb = b[k];
                if (va == null) return -1;
                if (vb == null) return 1;
                return String(va).localeCompare(String(vb), undefined, { numeric: true });
              },
        };
      });
      this.tableColumns = cols;
      this.tableRows = rows.map((row, i) => ({
        ...row,
        key: row.id != null ? `id-${row.id}` : row.key != null ? String(row.key) : `row-${i}`,
      }));
    },
    formatCellValue(val) {
      if (val == null) return '—';
      if (typeof val === 'object') {
        try {
          return JSON.stringify(val, null, 2);
        } catch (e) {
          return String(val);
        }
      }
      return String(val);
    },
  },
};
</script>

<style scoped>
.campaign-data-page {
  max-width: 1400px;
  margin: 0 auto;
}

.filter-card {
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.filter-card__title {
  margin-bottom: 16px;
}

.filter-card__hint {
  display: block;
  margin-top: 4px;
  color: rgba(0, 0, 0, 0.45);
  font-size: 13px;
}

.field-label {
  font-size: 12px;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.55);
  margin-bottom: 6px;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.field-label--placeholder {
  visibility: hidden;
}

.filter-row {
  margin-top: 4px;
}

.submit-col .ant-btn {
  height: 40px;
  font-weight: 500;
}

.results-card {
  border-radius: 8px;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  min-height: 280px;
}

.results-card__title {
  font-weight: 600;
}

.results-state {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 220px;
  padding: 48px 24px;
}

.results-empty {
  padding: 48px 24px 56px;
}

.table-wrap {
  padding: 0 8px 16px;
}

.campaign-data-table >>> .ant-table-thead > tr > th {
  background: #fafafa;
  font-weight: 600;
}

/* Spans from customRender live inside the table subtree */
.campaign-data-table >>> .cell-complex {
  font-family: 'SFMono-Regular', Menlo, Monaco, Consolas, monospace;
  font-size: 12px;
  white-space: pre-wrap;
  word-break: break-word;
  max-width: 320px;
  display: inline-block;
  vertical-align: top;
}
</style>
